import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronLeft, ChevronRight, Plus, Trash2, X } from "lucide-react";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import { IconTile, useIconUrlMap } from "./IconTile";
import {
  type FormFactor,
  type IconItem,
  type IconKind,
  SPECS,
  folderPageCapacity,
  MAX_FOLDER_PAGES,
} from "./types";
import { cn } from "@/lib/utils";

interface FolderModalProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  folder: Extract<IconItem, { Type: "Folder" }>;
  ff: FormFactor;
  onSave: (folder: Extract<IconItem, { Type: "Folder" }>) => void;
  onDelete: () => void;
  onWebClipIconKindResolved?: (url: string, kind: IconKind) => void;
}

function FolderCell({
  pageIdx,
  cellIdx,
  item,
  iconUrl,
  selected,
  onClick,
  onWebClipIconKindResolved,
}: {
  pageIdx: number;
  cellIdx: number;
  item?: IconItem;
  iconUrl?: string;
  selected: boolean;
  onClick: () => void;
  onWebClipIconKindResolved?: (url: string, kind: IconKind) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `folder-cell:${pageIdx}:${cellIdx}` });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "aspect-square rounded-xl flex items-center justify-center",
        isOver && "bg-primary/15 ring-2 ring-primary"
      )}
    >
      {item && (
        <IconTile
          item={item}
          iconUrl={iconUrl}
          size="sm"
          selected={selected}
          onClick={onClick}
          onWebClipIconKindResolved={onWebClipIconKindResolved}
        />
      )}
    </div>
  );
}

export function FolderModal({ open, onOpenChange, folder, ff, onSave, onDelete, onWebClipIconKindResolved }: FolderModalProps) {
  const [draft, setDraft] = useState(folder);
  const [pageIndex, setPageIndex] = useState(0);
  const [selected, setSelected] = useState<{ pageIdx: number; cellIdx: number } | null>(null);

  useEffect(() => {
    setDraft(folder);
    setPageIndex(0);
    setSelected(null);
  }, [folder, open]);

  const spec = SPECS[ff];
  const cap = folderPageCapacity(ff);
  const pages = draft.Pages.length > 0 ? draft.Pages : [[]];
  const page = pages[pageIndex] ?? [];
  const allItems = pages.flat();
  const iconMap = useIconUrlMap(allItems);

  function removeAt(pIdx: number, cIdx: number) {
    const next = pages.map((p, i) => (i === pIdx ? p.filter((_, j) => j !== cIdx) : p));
    setDraft({ ...draft, Pages: next });
    setSelected(null);
  }

  function addPage() {
    if (pages.length >= MAX_FOLDER_PAGES) return;
    const next = [...pages, []];
    setDraft({ ...draft, Pages: next });
    setPageIndex(next.length - 1);
  }
  function removePage() {
    if (pages.length <= 1) return;
    if (page.length > 0) return;
    const next = pages.filter((_, i) => i !== pageIndex);
    setDraft({ ...draft, Pages: next });
    setPageIndex(Math.max(0, pageIndex - 1));
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit folder</DialogTitle>
          <DialogDescription>
            Rename the folder, page through its contents, or remove icons. Drag from the App Library on the right of the editor to add more.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="folder-name">Folder name</Label>
            <Input
              id="folder-name"
              value={draft.DisplayName}
              onChange={(e) => setDraft({ ...draft, DisplayName: e.target.value })}
              placeholder="Productivity"
            />
          </div>

          <div className="rounded-2xl bg-muted/40 p-3">
            <div
              className="grid gap-1"
              style={{
                gridTemplateColumns: `repeat(${spec.folderGrid.cols}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${spec.folderGrid.rows}, minmax(0, 1fr))`,
              }}
            >
              {Array.from({ length: cap }).map((_, i) => {
                const item = page[i];
                const isSel = selected?.pageIdx === pageIndex && selected.cellIdx === i;
                return (
                  <FolderCell
                    key={i}
                    pageIdx={pageIndex}
                    cellIdx={i}
                    item={item}
                    iconUrl={item?.Type === "Application" ? iconMap[item.BundleID] : undefined}
                    selected={isSel}
                    onClick={() => setSelected({ pageIdx: pageIndex, cellIdx: i })}
                    onWebClipIconKindResolved={onWebClipIconKindResolved}
                  />
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8"
                onClick={() => setPageIndex(Math.max(0, pageIndex - 1))}
                disabled={pageIndex === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground">
                Page {pageIndex + 1} of {pages.length}
              </span>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8"
                onClick={() => setPageIndex(Math.min(pages.length - 1, pageIndex + 1))}
                disabled={pageIndex >= pages.length - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={addPage} disabled={pages.length >= MAX_FOLDER_PAGES}>
                <Plus className="h-4 w-4 mr-1" /> Page
              </Button>
              {selected && page[selected.cellIdx] && (
                <Button size="sm" variant="outline" onClick={() => removeAt(selected.pageIdx, selected.cellIdx)}>
                  <X className="h-4 w-4 mr-1" /> Remove icon
                </Button>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="justify-between sm:justify-between">
          <Button variant="destructive" onClick={onDelete}>
            <Trash2 className="h-4 w-4 mr-1" /> Delete folder
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={() => { onSave(draft); onOpenChange(false); }}>Save</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
